import { Routes as DomReroutes, Navigate, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { LoadingSpinner } from "@/components/atoms/LoadingSpinner";
import { PrivateRoute } from "./PrivateRoute";
import { SuspenseWrapper } from "@/components/common/SuspenseWrapper";
import StrokeOrderGeneratorPage from "@/pages/admin/StrokeOrderGeneratorPage";
import { MainPage } from "@/pages/MainPage";
import { ProfileSelectLearningPage } from "@/pages/auth/profile/ProfileSelectLearningPage";
import ExpertQuestionPage from "@/pages/expert/ExpertQuestionPage";
import ReviewDetailPage from "@/pages/review/ReviewDetailPage";
import { InsertDataPage } from "@/pages/admin/InsertDataPage";

// Lazy 로딩으로 컴포넌트 import
const IntroPage = lazy(() =>
  import("@/pages/IntroPage").then((module) => ({ default: module.IntroPage }))
);
const LoginPage = lazy(() =>
  import("@/pages/auth/LoginPage").then((module) => ({
    default: module.LoginPage,
  }))
);
const FindUserAccountPage = lazy(() =>
  import("@/pages/auth/find/FindUserAccountPage").then((module) => ({
    default: module.FindUserAccountPage,
  }))
);
const JoinFormPage = lazy(() =>
  import("@/pages/auth/join/JoinFormPage").then((module) => ({
    default: module.JoinFormPage,
  }))
);
const JoinForm02Page = lazy(() =>
  import("@/pages/auth/join/JoinForm02Page").then((module) => ({
    default: module.JoinForm02Page,
  }))
);
const JoinForm03Page = lazy(() =>
  import("@/pages/auth/join/JoinForm03Page").then((module) => ({
    default: module.JoinForm03Page,
  }))
);
const JoinSelectPage = lazy(() =>
  import("@/pages/auth/join/JoinSelectPage").then((module) => ({
    default: module.JoinSelectPage,
  }))
);
const JoinBuyPage = lazy(() =>
  import("@/pages/auth/join/JoinBuyPage").then((module) => ({
    default: module.JoinBuyPage,
  }))
);
const ProfileSelectPage = lazy(() =>
  import("@/pages/auth/profile/ProfileSelectPage").then((module) => ({
    default: module.ProfileSelectPage,
  }))
);
const ProfileSelectPageFlutter = lazy(() =>
  import("@/pages/auth/profile/ProfileSelectPageFlutter").then((module) => ({
    default: module.ProfileSelectPageFlutter,
  }))
);
const ProfileFormPage = lazy(() =>
  import("@/pages/auth/profile/ProfileFormPage").then((module) => ({
    default: module.ProfileFormPage,
  }))
);
const ProfileEditPage = lazy(() =>
  import("@/pages/auth/profile/ProfileEditPage").then((module) => ({
    default: module.ProfileEditPage,
  }))
);
const TestPage = lazy(() =>
  import("@/pages/test/TestPage").then((module) => ({
    default: module.TestPage,
  }))
);
const TestDetailPage = lazy(() =>
  import("@/pages/test/TestDetailPage").then((module) => ({
    default: module.TestDetailPage,
  }))
);
const TestPostPage = lazy(() =>
  import("@/pages/test/TestPostPage").then((module) => ({
    default: module.TestPostPage,
  }))
);
const TestResultPage = lazy(() =>
  import("@/pages/test/TestResultPage").then((module) => ({
    default: module.TestResultPage,
  }))
);
const TestListPage = lazy(() =>
  import("@/pages/test/TestListPage").then((module) => ({
    default: module.TestListPage,
  }))
);
const MyPage = lazy(() =>
  import("@/pages/mypage/MyPage").then((module) => ({ default: module.MyPage }))
);
const MyProfileSelectPage = lazy(() =>
  import("@/pages/mypage/MyProfileSelectPage").then((module) => ({
    default: module.MyProfileSelectPage,
  }))
);
const QnAPage = lazy(() =>
  import("@/pages/mypage/QnAPage").then((module) => ({
    default: module.QnAPage,
  }))
);
const InquirePage = lazy(() =>
  import("@/pages/mypage/inquire/InquirePage").then((module) => ({
    default: module.InquirePage,
  }))
);
const InquireWritePage = lazy(() =>
  import("@/pages/mypage/inquire/InquireWritePage").then((module) => ({
    default: module.InquireWritePage,
  }))
);
const SettingPage = lazy(() =>
  import("@/pages/mypage/setting/SettingPage").then((module) => ({
    default: module.SettingPage,
  }))
);
const TermsPage = lazy(() =>
  import("@/pages/mypage/setting/TermsPage").then((module) => ({
    default: module.TermsPage,
  }))
);
const CancelMembershipPage = lazy(() =>
  import("@/pages/mypage/setting/CancelMembershipPage").then((module) => ({
    default: module.CancelMembershipPage,
  }))
);
const PointHistoryPage = lazy(() =>
  import("@/pages/mypage/history/PointHistoryPage").then((module) => ({
    default: module.PointHistoryPage,
  }))
);
const CouponHistoryPage = lazy(() =>
  import("@/pages/mypage/history/CouponHistoryPage").then((module) => ({
    default: module.CouponHistoryPage,
  }))
);
const TotalAnalysisPage = lazy(() =>
  import("@/pages/mypage/analysis/TotalAnalysisPage").then((module) => ({
    default: module.TotalAnalysisPage,
  }))
);
const DevelopmentAnalysisPage = lazy(() =>
  import("@/pages/mypage/analysis/DevelopmentAnalysisPage").then((module) => ({
    default: module.DevelopmentAnalysisPage,
  }))
);
const StudyAnalysisPage = lazy(() =>
  import("@/pages/mypage/analysis/StudyAnalysisPage").then((module) => ({
    default: module.StudyAnalysisPage,
  }))
);
const DignosisTestPage = lazy(() =>
  import("@/pages/mypage/analysis/DignosisTestPage").then((module) => ({
    default: module.DignosisTestPage,
  }))
);
const TripPage = lazy(() =>
  import("@/pages/community/trip/TripPage").then((module) => ({
    default: module.TripPage,
  }))
);
const TripMapPage = lazy(() =>
  import("@/pages/community/trip/TripMapPage").then((module) => ({
    default: module.TripMapPage,
  }))
);
const TripMapInfoPage = lazy(() =>
  import("@/pages/community/trip/TripMapInfoPage").then((module) => ({
    default: module.TripMapInfoPage,
  }))
);
const BookPage = lazy(() =>
  import("@/pages/community/book/BookPage").then((module) => ({
    default: module.BookPage,
  }))
);
const BookInfoPage = lazy(() =>
  import("@/pages/community/book/BookInfoPage").then((module) => ({
    default: module.BookInfoPage,
  }))
);
const GrowPage = lazy(() =>
  import("@/pages/community/grow/GrowPage").then((module) => ({
    default: module.GrowPage,
  }))
);

const GrowListPage = lazy(() =>
  import("@/pages/community/grow/GrowListPage").then((module) => ({
    default: module.GrowListPage,
  }))
);

const GrowArticlePage = lazy(() =>
  import("@/pages/community/grow/GrowArticlePage").then((module) => ({
    default: module.GrowArticlePage,
  }))
);
const GrowHomeplayPage = lazy(() =>
  import("@/pages/community/grow/GrowHomeplayPage").then((module) => ({
    default: module.GrowHomeplayPage,
  }))
);
const InfoSharePage = lazy(() =>
  import("@/pages/community/information/InfoSharePage").then((module) => ({
    default: module.InfoSharePage,
  }))
);
const InfoShareListPage = lazy(() =>
  import("@/pages/community/information/InfoShareListPage").then((module) => ({
    default: module.InfoShareListPage,
  }))
);
const InfoWritePage = lazy(() =>
  import("@/pages/community/information/InfoWritePage").then((module) => ({
    default: module.InfoWritePage,
  }))
);
const InfoBoardPage = lazy(() =>
  import("@/pages/community/information/InfoBoardPage").then((module) => ({
    default: module.InfoBoardPage,
  }))
);
const InfoPostPage = lazy(() =>
  import("@/pages/community/information/InfoPostPage").then((module) => ({
    default: module.InfoPostPage,
  }))
);
const InfoTipPage = lazy(() =>
  import("@/pages/community/information/InfoTipPage").then((module) => ({
    default: module.InfoTipPage,
  }))
);
const CounselingPage = lazy(() =>
  import("@/pages/counseling/CounselingPage").then((module) => ({
    default: module.CounselingPage,
  }))
);
const ContestPage = lazy(() =>
  import("@/pages/community/contest/ContestPage").then((module) => ({
    default: module.ContestPage,
  }))
);
const ContestPostPage = lazy(() =>
  import("@/pages/community/contest/ContestPostPage").then((module) => ({
    default: module.ContestPostPage,
  }))
);
const ContestBoardPage = lazy(() =>
  import("@/pages/community/contest/ContestBoardPage").then((module) => ({
    default: module.ContestBoardPage,
  }))
);
const ContestWritePage = lazy(() =>
  import("@/pages/community/contest/ContestWritePage").then((module) => ({
    default: module.ContestWritePage,
  }))
);

interface RouterItem {
  path: string;
  element: React.ReactNode;
  withAuthorization: boolean;
  pageTitle?: string;
}

const routerItems: RouterItem[] = [
  /* 인트로 */
  {
    path: "/intro",
    element: (
      <SuspenseWrapper>
        <IntroPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },

  /* 메인 홈 */
  {
    path: "/home",
    element: (
      <SuspenseWrapper>
        <MainPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },

  /* 로그인 */
  {
    path: "/auth/login",
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },

  /* 아이디/비밀번호 찾기 */
  {
    path: "/auth/find/user-account",
    element: (
      <SuspenseWrapper>
        <FindUserAccountPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  /* 회원가입 */
  {
    path: "/auth/join/form",
    element: (
      <SuspenseWrapper>
        <JoinFormPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/auth/join/form02",
    element: (
      <SuspenseWrapper>
        <JoinForm02Page />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/auth/join/form03",
    element: (
      <SuspenseWrapper>
        <JoinForm03Page />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/auth/join/select",
    element: (
      <SuspenseWrapper>
        <JoinSelectPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/auth/join/buy",
    element: (
      <SuspenseWrapper>
        <JoinBuyPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },

  /* 프로필 설정 */
  {
    path: "/auth/profile/select",
    element: (
      <SuspenseWrapper>
        <ProfileSelectPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/auth/profile/edit",
    element: (
      <SuspenseWrapper>
        <ProfileEditPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  /** 탭 클릭시 > 학습 컨텐츠로 넘어갈 수 있도록 페이지 분리 */
  {
    path: "/auth/profile/select/learning",
    element: (
      <SuspenseWrapper>
        <ProfileSelectLearningPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  /* 프로필 설정 태블릿 그로우 러닝 화면에서 프로필 선택 페이지 */
  {
    path: "/auth/profile/select/flutter",
    element: (
      <SuspenseWrapper>
        <ProfileSelectPageFlutter />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/auth/profile/form",
    element: (
      <SuspenseWrapper>
        <ProfileFormPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },

  /* 진단테스트 */
  {
    path: "/test",
    element: (
      <SuspenseWrapper>
        <TestPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/test/detail",
    element: (
      <SuspenseWrapper>
        <TestDetailPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/test/post",
    element: (
      <SuspenseWrapper>
        <TestPostPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/test/result",
    element: (
      <SuspenseWrapper>
        <TestResultPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/test/list",
    element: (
      <SuspenseWrapper>
        <TestListPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  /* 커뮤니티 */
  {
    path: "/community/trip",
    element: (
      <SuspenseWrapper>
        <TripPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/trip/map",
    element: (
      <SuspenseWrapper>
        <TripMapPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/trip/:id",
    element: (
      <SuspenseWrapper>
        <TripMapInfoPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/book",
    element: (
      <SuspenseWrapper>
        <BookPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/book/:id",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <BookInfoPage />
      </Suspense>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/grow",
    element: (
      <SuspenseWrapper>
        <GrowPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/grow/list",
    element: (
      <SuspenseWrapper>
        <GrowListPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/grow/:id",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <GrowArticlePage />
      </Suspense>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/grow/homeplay",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <GrowHomeplayPage />
      </Suspense>
    ),
    withAuthorization: false,
  },
  /** 커뮤니티 > 육아 */
  {
    path: "/community/info",
    element: (
      <SuspenseWrapper>
        <InfoSharePage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/info/list",
    element: (
      <SuspenseWrapper>
        <InfoShareListPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/info/write",
    element: (
      <SuspenseWrapper>
        <InfoWritePage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/info/board",
    element: (
      <SuspenseWrapper>
        <InfoBoardPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/info/board/:id",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <InfoPostPage />
      </Suspense>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/info/tip/:id",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <InfoTipPage />
      </Suspense>
    ),
    withAuthorization: false,
  },
  /** 커뮤니티 > 상담소 */
  {
    path: "community/counseling",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <CounselingPage />
      </Suspense>
    ),
    withAuthorization: false,
  },
  /** 커뮤니티 > 콘테스트 */
  {
    path: "/community/contest",
    element: (
      <SuspenseWrapper>
        <ContestPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/contest/write",
    element: (
      <SuspenseWrapper>
        <ContestWritePage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/contest/board/:id",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <ContestPostPage />
      </Suspense>
    ),
    withAuthorization: false,
  },
  {
    path: "/community/contest/board",
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <ContestBoardPage />
      </Suspense>
    ),
    withAuthorization: false,
  },

  /* 마이페이지 */
  {
    path: "/mypage",
    element: (
      <SuspenseWrapper>
        <MyPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/select",
    element: (
      <SuspenseWrapper>
        <MyProfileSelectPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/admin/stroke-order-generator",
    element: (
      <SuspenseWrapper>
        <StrokeOrderGeneratorPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/admin/insert-data",
    element: (
      <SuspenseWrapper>
        <InsertDataPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/qna",
    element: (
      <SuspenseWrapper>
        <QnAPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/inquire",
    element: (
      <SuspenseWrapper>
        <InquirePage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/inquire/write",
    element: (
      <SuspenseWrapper>
        <InquireWritePage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/setting",
    element: (
      <SuspenseWrapper>
        <SettingPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/setting/terms",
    element: (
      <SuspenseWrapper>
        <TermsPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/setting/cancel-membership",
    element: (
      <SuspenseWrapper>
        <CancelMembershipPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/history/point",
    element: (
      <SuspenseWrapper>
        <PointHistoryPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/history/coupon",
    element: (
      <SuspenseWrapper>
        <CouponHistoryPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/analysis/total",
    element: (
      <SuspenseWrapper>
        <TotalAnalysisPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/analysis/development",
    element: (
      <SuspenseWrapper>
        <DevelopmentAnalysisPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/analysis/study",
    element: (
      <SuspenseWrapper>
        <StudyAnalysisPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  {
    path: "/mypage/analysis/result",
    element: (
      <SuspenseWrapper>
        <DignosisTestPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  /** 전문의에게 물어보기 페이지 */
  {
    path: "/expert/question",
    element: (
      <SuspenseWrapper>
        <ExpertQuestionPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  /** 전문의 소개 페이지 */
  {
    path: "/expert/info",
    element: (
      <SuspenseWrapper>
        <ExpertQuestionPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
  /** 후기 더보기 페이지 */
  {
    path: "/review/detail",
    element: (
      <SuspenseWrapper>
        <ReviewDetailPage />
      </SuspenseWrapper>
    ),
    withAuthorization: false,
  },
];

export function Routes() {
  return (
    <DomReroutes>
      <Route path="/" element={<PrivateRoute />} />
      <Route
        path="/community"
        element={<Navigate replace to="/community/trip" />}
      />

      {routerItems
        .filter((route) => !route.withAuthorization)
        .map((route: RouterItem) => {
          return (
            <Route key={route.path} path={route.path} element={route.element} />
          );
        })}
    </DomReroutes>
  );
}
